import httpService from "./HttpService";
import {
    adminGuidesControllerGetGuides,
    adminGuidesControllerGetGuide,
    adminGuidesControllerDeleteGuide,
} from "../api/generated";

// Migrated to the generated client. The endpoint's query is FLAT (pageId/pageSize/keyword/
// categoryId/tagIds — no nested Spring `pageable`), so it serializes correctly through the SDK.
// The server no longer accepts a `companyId` query param (company is derived from the authed
// principal — the generated query type has no companyId), so the old `companyId` arg is
// accepted for signature parity but not forwarded. Returns {data: PageGuideDto} so callers
// keep reading `res.data.content/totalElements/totalPages` unchanged.
export const getGuides = async (pageId = 0, pageSize = 20, keyword, companyId, categoryId, tagIds) => {
    const {data, error} = await adminGuidesControllerGetGuides({
        query: {
            pageId,
            pageSize,
            ...(keyword ? {keyword} : {}),
            ...(categoryId ? {categoryId} : {}),
            ...(tagIds?.length ? {tagIds} : {}),
        },
    });
    if (error) throw error;
    return {data};
};

// Migrated. Returns {data: GuideDto}; callers read `res.data`.
export const getGuideById = async (id) => {
    const {data, error} = await adminGuidesControllerGetGuide({path: {id}});
    if (error) throw error;
    return {data};
};

// TODO(openapi-migration): multipart/file upload (pdf/video/thumbnail + JSON metaData part).
// The generated SDK models the body as {pdf, video, thumbnail, metaData: string}, but this
// stays on httpService/FormData so the axios error shape (error.response.data.errorMessage)
// the create/edit dialog reads is preserved.
export const saveGuide = async (guideData) => {
    const token = localStorage.getItem("auth");

    const formData = new FormData();

    if (guideData.pdfFile) {
        formData.append("pdf", guideData.pdfFile);
    }

    if (guideData.video) {
        formData.append("video", guideData.video);
    }

    if (guideData.thumbnail) {
        formData.append("thumbnail", guideData.thumbnail);
    }

    const metaData = {
        title: guideData.title,
        description: guideData.description,
        companyId: guideData.companyId || null,
        pdfFileName: guideData.pdfFileName,
        categoryId: guideData.categoryId || null,
        tagIds: guideData.tagIds || [],
        // Optional external website link. Empty string clears it server-side.
        linkUrl: guideData.linkUrl || null
    };

    // Only send videoUrl OR videoFileName, not both
    if (guideData.videoFilename) {
        metaData.videoFileName = guideData.videoFilename;
    } else if (guideData.videoUrl !== undefined) {
        metaData.videoUrl = guideData.videoUrl || null;
    }

    // Send thumbnailUrl for YouTube videos
    if (guideData.thumbnailUrl) {
        metaData.thumbnailUrl = guideData.thumbnailUrl;
    }

    formData.append("metaData", JSON.stringify(metaData));

    return await httpService.post("admin/guides", formData, token);
};

// TODO(openapi-migration): multipart/file upload (pdf/video/thumbnail + JSON metaData part).
// Stays on httpService/FormData; see saveGuide.
export const updateGuide = async (id, guideData) => {
    const token = localStorage.getItem("auth");

    const formData = new FormData();

    if (guideData.pdfFile) {
        formData.append("pdf", guideData.pdfFile);
    }

    if (guideData.video) {
        formData.append("video", guideData.video);
    }

    if (guideData.thumbnail) {
        formData.append("thumbnail", guideData.thumbnail);
    }

    const metaData = {};

    if (guideData.title !== undefined) {
        metaData.title = guideData.title;
    }

    if (guideData.description !== undefined) {
        metaData.description = guideData.description;
    }

    if (guideData.videoUrl !== undefined) {
        metaData.videoUrl = guideData.videoUrl || null;
    }

    if (guideData.videoFilename !== undefined) {
        metaData.videoFileName = guideData.videoFilename;
    }

    // Send thumbnailUrl for YouTube videos
    if (guideData.thumbnailUrl) {
        metaData.thumbnailUrl = guideData.thumbnailUrl;
    }

    if (guideData.removeVideo) {
        metaData.removeVideo = true;
    }

    if (guideData.removeThumbnail) {
        metaData.removeThumbnail = true;
    }

    if (guideData.categoryId !== undefined) {
        metaData.categoryId = guideData.categoryId;
    }

    if (guideData.tagIds !== undefined) {
        metaData.tagIds = guideData.tagIds;
    }

    if (guideData.pdfFileName !== undefined) {
        metaData.pdfFileName = guideData.pdfFileName;
    }

    // Sent verbatim (no `|| null` coalescing): "" is the explicit clear signal the
    // server maps to null, while omitting the key leaves the existing link untouched.
    if (guideData.linkUrl !== undefined) {
        metaData.linkUrl = guideData.linkUrl;
    }

    formData.append("metaData", JSON.stringify(metaData));

    return await httpService.patch(`admin/guides/${id}`, formData, token);
};

// Migrated. Old code returned the raw axios response; the delete result is only awaited
// (no field read), so we return {data} for parity with the other migrated getters.
export const deleteGuide = async (id) => {
    const {data, error} = await adminGuidesControllerDeleteGuide({path: {id}});
    if (error) throw error;
    return {data};
};
